/**
 * Strategy Brief — consolidated AI report per analysis.
 *
 * Two phases:
 *  1. POST /api/strategy-brief/generate → { job_id }
 *  2. Poll GET /api/strategy-brief/job/:job_id until status=complete
 *  3. Fetch GET /api/strategy-brief/:brief_id and render
 *
 * Renders 9 ResultSection collapsibles:
 *   - Problem statement
 *   - Business currency (3 bullets)
 *   - Superpower play
 *   - Network moves
 *   - Company intel
 *   - ATS gaps
 *   - Adjacent roles
 *   - Fit score + actions
 *   - Next step CTA → /studio?analysis_id=
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import ResultSection from "../components/ResultSection";
import { ReportTabStrip } from "../components/reports/ScoreAnalysis";
import SectionExpandToolbar from "../components/SectionExpandToolbar";
import ResmoMascot from "../components/ResmoMascot";
import { api, friendlyAxiosError } from "../lib/api";
import {
  Loader, Sparkles, Target, Zap, Footprints, Building2, AlertTriangle,
  Compass, Gauge, Wand2, ArrowRight, RefreshCw, Mail,
} from "lucide-react";
import EmailReportModal from "../components/EmailReportModal";
import {
  RK, ReportLabel, ReportList, ReportRichList, ReportGrid, ReportCallout,
} from "../components/reports/ReportKit";

const GROUP = "strategy-brief";

const STAGES = {
  queued:   "Queued — Resmo is preparing your file",
  thinking: "Resmo is reading your resume + job description…",
  done:     "Brief ready",
  error:    "Something went wrong",
};

export default function StrategyBrief() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // ?analysis_id=<id> launches a NEW brief; /:id loads an existing one.
  const newAnalysisId = new URLSearchParams(location.search).get("analysis_id") || "";

  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const pollTimerRef = useRef(null);

  // Load an existing brief by id, or kick off a new generate job.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (id) {
          const { data } = await api.get(`/strategy-brief/${id}`);
          if (alive) { setBrief(data); setLoading(false); }
        } else if (newAnalysisId) {
          setLoading(true);
          const { data } = await api.post("/strategy-brief/generate", { analysis_id: newAnalysisId });
          if (alive) startPoll(data.job_id);
        } else {
          setLoading(false);
          setError("No analysis selected. Open a saved tailored resume and click 'Build a strategy brief'.");
        }
      } catch (e) {
        if (alive) { setError(friendlyAxiosError(e)); setLoading(false); }
      }
    })();
    return () => {
      alive = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, newAnalysisId]);

  // Tick the elapsed-seconds counter while polling so we can drive a real
  // % progress bar instead of a vague "this takes ~60-90 seconds" hint.
  useEffect(() => {
    if (!loading || brief) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [loading, brief]);

  const startPoll = (job_id) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/strategy-brief/job/${job_id}`);
        setJobStatus(data);
        if (data.status === "complete" && data.brief_id) {
          // Replace URL so refresh shows the brief
          navigate(`/strategy-brief/${data.brief_id}`, { replace: true });
          const { data: full } = await api.get(`/strategy-brief/${data.brief_id}`);
          setBrief(full);
          setLoading(false);
          return;
        }
        if (data.status === "error") {
          setError(data.error || "The coach hit an issue. Please try again.");
          setLoading(false);
          return;
        }
        if (attempts > 90) { // ~7.5 minutes
          setError("This is taking longer than usual. Try again later.");
          setLoading(false);
          return;
        }
        pollTimerRef.current = setTimeout(tick, 5000);
      } catch (e) {
        setError(friendlyAxiosError(e));
        setLoading(false);
      }
    };
    tick();
  };

  if (loading) {
    // Drive a % progress signal off the polled stage + elapsed seconds.
    const stage = jobStatus?.stage || "queued";
    const stageBand = {
      queued:   { from: 4,  to: 16, perSec: 1.5 },
      thinking: { from: 16, to: 92, perSec: 0.9 },
      done:     { from: 100, to: 100, perSec: 0 },
      error:    { from: 0,  to: 0,  perSec: 0 },
    };
    const band = stageBand[stage] || stageBand.queued;
    const progressPct = stage === "done" ? 100
      : Math.max(band.from, Math.min(band.to, Math.round(band.from + elapsed * band.perSec)));

    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
          <div className="rm-card-static p-8 text-center" data-testid="brief-loading">
            <ResmoMascot state="thinking" size={140} />
            <div className="font-display text-2xl uppercase mt-3">Building your strategy brief.</div>
            <p className="text-sm mt-2" style={{ color: "#475569" }}>
              {jobStatus?.stage ? (STAGES[jobStatus.stage] || jobStatus.stage) : "Spinning up the coach…"}
            </p>
            <div className="font-display text-center mt-4"
                 style={{ fontSize: "2.4rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}
                 data-testid="brief-progress-percent">
              {progressPct}<span style={{ fontSize: "1.3rem", color: "#475569" }}>%</span>
            </div>
            <div className="mt-3 h-3 rounded-full overflow-hidden max-w-md mx-auto"
                 style={{ background: "#FFF2EC", border: "2px solid #0F1B2D" }}>
              <div className="h-full transition-all duration-700"
                   style={{
                     width: `${progressPct}%`,
                     background: "linear-gradient(90deg,#FF6A00,#B45309)",
                   }}
                   data-testid="brief-progress-bar"
              />
            </div>
            <p className="text-[11px] font-mono-rm uppercase tracking-widest mt-4" style={{ color: "#94A3B8" }}>
              · You can leave the tab — we'll have it ready when you return.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !brief) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-12">
          <div className="rm-card-static p-6" data-testid="brief-error">
            <div className="rm-label" style={{ color: "#991B1B" }}>· Couldn't build the brief</div>
            <div className="font-display text-xl uppercase mt-1">Something tripped up the coach.</div>
            <p className="text-sm mt-2" style={{ color: "#475569" }}>{error || "Try again in a minute."}</p>
            <button onClick={() => navigate("/")} className="rm-btn rm-btn-primary text-xs px-3 py-2 mt-4">
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const b = brief.brief;
  const fitColor =
    b.fit_score.score >= 85 ? "#0E5A4A" :
    b.fit_score.score >= 70 ? "#20C1A7" :
    b.fit_score.score >= 55 ? "#CA8A04" : "#B91C1C";

  return (
    <div className="min-h-screen rm-paper" data-testid="strategy-brief-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Header */}
        <div className="rm-card-static p-5 md:p-6 mb-4"
             style={{ background: "linear-gradient(135deg, #0F1B2D 0%, #1E3A5F 100%)", color: "white" }}>
          <div className="font-mono-rm uppercase text-[10px] tracking-widest"
               style={{ color: "#FFC145", fontWeight: 700 }}>
            · Role Strategy Brief
          </div>
          <h1 className="font-display uppercase leading-tight mt-1"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", letterSpacing: "-0.02em", color: "white" }}>
            {brief.job_title || "—"} <span style={{ color: "#FFC145" }}>@</span> {brief.company_name || "—"}
          </h1>
          <p className="text-sm mt-2" style={{ color: "#E2E8F0" }}>
            A consolidated coach's briefing combining your resume, skill assessment, job description, and network — so you walk in prepared.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 mb-2 flex-wrap">
          <SectionExpandToolbar
            group={GROUP}
            targetSelector='[data-testid="strategy-brief-sections"]'
            pdfTitle={`Resmo Strategy Brief — ${brief.job_title || ""}`}
            pdfFilename={`resmo-strategy-brief-${(brief.job_title || "role").replace(/\s+/g, "_")}.pdf`}
          />
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-1"
            style={{
              background: "#FF6A00", color: "white",
              border: "2px solid #0F1B2D", borderRadius: 6, fontWeight: 700,
            }}
            data-testid="brief-email-btn"
            title="Email this brief to a coach or friend"
          >
            <Mail size={11} /> Email
          </button>
        </div>

        <ReportTabStrip
          testIdPrefix="brief-tab"
          tabs={[
            { id: "problem", label: "Problem", sectionTestId: "brief-problem-section" },
            { id: "currency", label: "Currency", sectionTestId: "brief-currency-section" },
            { id: "superpower", label: "Superpower", sectionTestId: "brief-superpower-section" },
            { id: "network", label: "Network", sectionTestId: "brief-network-section" },
            { id: "company", label: "Company", sectionTestId: "brief-company-section" },
            { id: "ats", label: "ATS Gaps", sectionTestId: "brief-ats-section" },
            { id: "adjacent", label: "Adjacent", sectionTestId: "brief-adjacent-section" },
            { id: "fit", label: "Fit Score", sectionTestId: "brief-fit-section" },
          ]}
        />
        <div data-testid="strategy-brief-sections" className="space-y-4">
          {/* 1. Problem statement */}
          <ResultSection
            title="The real problem this role solves"
            eyebrow="Section 1 · Problem statement"
            accentGradient="linear-gradient(135deg,#F97316 0%,#B91C1C 100%)"
            defaultOpen
            group={GROUP}
            testid="brief-problem-section"
          >
            <p className="text-[15px] leading-relaxed" style={{ color: "#0F1B2D" }}>
              {b.problem_statement}
            </p>
          </ResultSection>

          {/* 2. Business currency */}
          <ResultSection
            title="Your business currency"
            eyebrow="Section 2 · How you solve their problem"
            accentGradient="linear-gradient(135deg,#FFC145 0%,#CA8A04 100%)"
            count={(b.business_currency || []).length}
            defaultOpen
            group={GROUP}
            testid="brief-currency-section"
          >
            <ReportRichList
              icon={<Target size={11} />}
              title="How you solve their problem"
              color={RK.amber}
              items={b.business_currency.map((bc) => ({ heading: bc.bullet, body: bc.how_it_solves }))}
            />
          </ResultSection>

          {/* 3. Superpower play */}
          <ResultSection
            title="Your superpower in action"
            eyebrow="Section 3 · How to wield it"
            accentGradient="linear-gradient(135deg,#FF6A00 0%,#B45309 100%)"
            group={GROUP}
            testid="brief-superpower-section"
          >
            <div className="rm-card-static p-4" style={{ background: "linear-gradient(135deg,#FF6A00,#B45309)", color: "white" }}>
              <div className="flex items-center gap-2 mb-2">
                <Zap size={14} style={{ color: "#FFC145" }} />
                <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#FFC145", fontWeight: 700 }}>
                  · Your superpower
                </div>
              </div>
              <div className="font-display text-xl uppercase leading-tight">{b.superpower_play.superpower}</div>
            </div>
            <div className="mt-3">
              <ReportLabel color={RK.orange} className="mb-1">How to wield it</ReportLabel>
              <p className="text-[13px] leading-snug" style={{ color: RK.body }}>
                {b.superpower_play.how_to_wield_it}
              </p>
            </div>
          </ResultSection>

          {/* 4. Network moves */}
          {b.network_moves.length > 0 && (
            <ResultSection
              title="Network moves to make this week"
              eyebrow="Section 4 · People who can move the needle"
              accentGradient="linear-gradient(135deg,#20C1A7 0%,#0E5A4A 100%)"
              count={b.network_moves.length}
              group={GROUP}
              testid="brief-network-section"
            >
              <ReportRichList
                icon={<Footprints size={11} />}
                title="People who can move the needle"
                color={RK.green}
                items={b.network_moves.map((nm) => ({ heading: nm.contact_name, body: nm.why, extra: `Ask: ${nm.ask}` }))}
              />
            </ResultSection>
          )}

          {/* 5. Company intel */}
          <ResultSection
            title="Company intel"
            eyebrow="Section 5 · Culture, size, positioning"
            accentGradient="linear-gradient(135deg,#3B82F6 0%,#1E40AF 100%)"
            group={GROUP}
            testid="brief-company-section"
          >
            <ReportGrid>
              <div>
                <ReportLabel icon={<Building2 size={11} />} color={RK.blue} className="mb-1">Culture</ReportLabel>
                <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{b.company_intel.culture}</p>
              </div>
              <div>
                <ReportLabel color={RK.blue} className="mb-1">Size &amp; stage</ReportLabel>
                <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{b.company_intel.size_stage}</p>
              </div>
            </ReportGrid>
            <ReportCallout title="How to position" className="mt-4">
              {b.company_intel.positioning_tip}
            </ReportCallout>
          </ResultSection>

          {/* 6. ATS gaps */}
          <ResultSection
            title="ATS gaps & what to fix"
            eyebrow="Section 6 · Resume gaps prioritized"
            accentGradient="linear-gradient(135deg,#B91C1C 0%,#7F1D1D 100%)"
            count={b.ats_gaps.length}
            group={GROUP}
            testid="brief-ats-section"
          >
            <ReportRichList
              icon={<AlertTriangle size={11} />}
              title="Gaps prioritized"
              color={RK.red}
              numbered
              items={b.ats_gaps.map((g) => ({ heading: g.gap, body: g.why, extra: `Fix: ${g.fix}` }))}
            />
          </ResultSection>

          {/* 7. Adjacent roles */}
          <ResultSection
            title="Other roles you'd fit"
            eyebrow="Section 7 · Backup paths"
            accentGradient="linear-gradient(135deg,#10B981 0%,#047857 100%)"
            count={b.adjacent_roles.length}
            group={GROUP}
            testid="brief-adjacent-section"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {b.adjacent_roles.map((r, i) => (
                <div key={i} className="p-3 rounded" style={{ background: "#D1FAE5", border: "2px solid #047857" }}>
                  <div className="flex items-start gap-2">
                    <Compass size={14} style={{ color: "#047857" }} className="mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-display uppercase text-sm leading-tight">{r.role}</div>
                      <p className="text-[12px] mt-1" style={{ color: "#475569" }}>{r.why_fit}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ResultSection>

          {/* 8. Fit score */}
          <ResultSection
            title={`Overall fit · ${b.fit_score.score}/100`}
            eyebrow="Section 8 · Score + actions to lift it"
            accentGradient={`linear-gradient(135deg, ${fitColor} 0%, #0F1B2D 100%)`}
            defaultOpen
            group={GROUP}
            testid="brief-fit-section"
          >
            <div className="flex items-center gap-4 flex-wrap mb-3">
              <div className="flex items-center justify-center font-display"
                   style={{
                     width: 80, height: 80, borderRadius: "50%",
                     background: `conic-gradient(${fitColor} ${b.fit_score.score}%, #F1F5F9 ${b.fit_score.score}% 100%)`,
                     border: `3px solid ${fitColor}`,
                   }}>
                <div className="flex items-center justify-center"
                     style={{ width: 64, height: 64, borderRadius: "50%", background: "white", color: fitColor, fontSize: "1.4rem" }}
                     data-testid="brief-fit-score">
                  {b.fit_score.score}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: fitColor, fontWeight: 700 }}>
                  <Gauge size={11} /> Honest assessment
                </div>
                <p className="text-[13px] mt-1" style={{ color: "#0F1B2D" }}>{b.fit_score.rationale}</p>
              </div>
            </div>
            <ReportCallout title="Do these 3 things TODAY to lift the score" bg="#FFF2EC" border={RK.orange} color={RK.amber}>
              <ReportList ordered color={RK.orange} items={b.fit_score.actions} />
            </ReportCallout>
          </ResultSection>
        </div>

        {/* Next-step CTA — full-bleed banner */}
        <div className="rm-card-static p-5 md:p-6 mt-5"
             style={{
               background: "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
               color: "white",
               boxShadow: "8px 8px 0 0 #0F1B2D",
             }}
             data-testid="brief-next-step">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1" style={{ color: "#FFC145", fontWeight: 700 }}>
                · Ready to apply? · ~{b.next_step.estimated_time}
              </div>
              <div className="font-display uppercase text-xl leading-tight">{b.next_step.headline}</div>
            </div>
            <button
              onClick={() => navigate(`/studio?analysis_id=${brief.analysis_id}`)}
              className="rm-btn rm-btn-secondary text-sm px-4 py-3"
              style={{ background: "white", color: "#0F1B2D" }}
              data-testid="brief-open-studio-btn"
            >
              <Wand2 size={14} /> {b.next_step.cta}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={() => navigate(`/strategy-brief?analysis_id=${brief.analysis_id}`)}
            className="rm-btn rm-btn-ghost text-xs px-3 py-2"
            data-testid="brief-regenerate-btn"
            title="Regenerate the brief (10 credits)"
          >
            <RefreshCw size={12} /> Regenerate brief
          </button>
          <button
            onClick={() => navigate(`/dashboard/${brief.analysis_id}`)}
            className="rm-btn rm-btn-ghost text-xs px-3 py-2"
            data-testid="brief-back-analysis-btn"
          >
            <ArrowRight size={12} /> Back to tailored resume
          </button>
        </div>
      </div>

      <EmailReportModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        kindLabel="Strategy Brief"
        sharePath={`/strategy-brief/${brief.id}/share`}
        emailPath={`/strategy-brief/${brief.id}/email`}
        publicPath="/strategy-brief/share/"
      />
    </div>
  );
}
